/**
 * Partner Notes Section Controller
 *
 * The sidebar "Notes" page: the note inbox a coupled user exchanges with
 * their linked partner (backend: partner memos). An Inbox/Sent toggle sits
 * above the shared ResultsFilterBar (Active/Archived filter + a Select
 * action) over compact title-only rows on the shared session-card system
 * (patterns-page hover + dividers): relative date on the right that swaps to
 * a chevron on hover; starred rows carry a star marker; unread received
 * notes use a medium-weight title with a blue UNREAD pill until opened (markMemoViewed on
 * detail-open, mirroring the app's viewed-state badges). Select action
 * enters a multi-select mode — checkboxes on every row, the bar becomes
 * "N selected" + Star/Unstar (received) or Revoke (sent) + Archive/Unarchive
 * + Delete + Cancel; Delete and Revoke confirm via modalService, Star
 * doesn't; applying any action exits select mode. Detail opens in the right
 * panel with a header action: "Discuss Note" (received, ADVICE memo_entry)
 * or "Retract Note" (sent, soft-revoke → Withdrawn).
 *
 * The whole surface is couple-gated: the sidebar item is hidden when unlinked
 * (see Sidebar.setItemVisibility + the partner:unlinked subscription),
 * `activate()` redirects home without an active `coupleId`, and the backend
 * couple-scopes both list reads so an unlinked user gets `[]` regardless.
 *
 * Naming: the frontend says "notes" everywhere; the backend keeps "memo"
 * (PartnerMemo rows, /couples/memos routes, memo_entry sessions). The
 * DiscoveryAPI wrapper methods retain the backend names as the boundary.
 * Delete is PER-SIDE (hides the note from this user's list only); Star is a
 * recipient-side marker that never ships to the sender.
 *
 * URL contract (panel-routed detail, Sessions-style):
 *   /notes                     → Received list, Active
 *   /notes?list=sent           → Sent list
 *   /notes?state=archived      → the caller-side Archived view
 *   /notes?list=X&note=<id>    → detail panel open for that note
 * Sorting is fixed newest-first; select-mode is in-memory only. Archive is
 * PER-SIDE like delete (moves the note between YOUR Active/Archived views
 * only; the backend maps the side-specific stamp to a unified `archivedAt`).
 *
 * @module sections/PartnerNotesSection
 */

import page from '../vendor/page.js';
import { eventBus, escapeHtml } from '../core/utils.js';
import {
  emitContentStarred,
  emitContentArchived,
  emitContentDeleted,
  emitContentViewed,
  CONTENT_STARRED,
  CONTENT_ARCHIVED,
  CONTENT_DELETED,
  CONTENT_VIEWED
} from '../core/contentSync.js';
import { DiscoveryAPI } from '../core/discoveryApi.js';
import { ResultsFilterBar } from '../components/ResultsFilterBar.js';
import { renderEmptyState } from '../components/emptyState.js';
import {
  buildPageInfoPanel,
  renderPageInfoTitle,
  renderPageInfoFlow,
  renderPageInfoFaq
} from '../components/pageInfoPanel.js';
import { modalService } from '../services/ModalService.js';
import { launchMemoDiscussAdvice } from '../orchestration/memoEntryLauncher.js';
import { launchComposeNoteAdvice } from '../orchestration/composeNoteLauncher.js';
import { buildNoteDetailPayload, noteCardTitle } from '../components/partnerNotesDrillHelper.js';
import { renderRowKebab, mountSelectToolbar, RowMenuController } from '../components/optionsMenu.js';
import { formatRelativeDate } from '../utils/timeFormat.js';
import { notifyArchived } from '../utils/archiveFeedback.js';
import { StickyStack } from '../utils/StickyStack.js';
import { Tooltip } from '../utils/Tooltip.js';

/** Right-panel contentType for the routed note detail. */
const CONTENT_TYPE = 'partner-note-detail';

/**
 * Right-panel contentType for the "About Notes" page-info panel. The `-info`
 * suffix is load-bearing, not cosmetic: `PageManager._isPageInfoContentType`
 * matches on it to give the active section a chance to clear its routed detail
 * before the panel content swaps.
 */
const INFO_CONTENT_TYPE = 'notes-info';

/** Small inline star (filled) — shared sprite symbol (also used by /search). */
const STAR_SVG = `<svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true"><use href="#icon-star"/></svg>`;

/** Right-side chevron shown on row hover (outside select mode). */
const CHEVRON_SVG = `<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`;

/**
 * Leading-slot type icon (mail glyph — matches /search note cards). Same
 * unread-dot treatment /search's rows now carry (search-notes-dots-and-
 * practices-sidebar.md, Part I) — nested directly inside
 * `.session-card__type-icon` (that class carries no base opacity, so no
 * sibling-wrapper trick is needed; shared CSS lives in sessions.css).
 * @param {boolean} [unread=false] - Whether to render the dot
 * @returns {string} HTML string
 */
function noteIconHtml(unread = false) {
  const dot = unread ? '<span class="session-card__type-icon-dot" aria-hidden="true"></span>' : '';
  return `<span class="session-card__type-icon" aria-hidden="true"><svg width="21" height="21"><use href="#icon-email"/></svg>${dot}</span>`;
}

export class PartnerNotesSection {
  /**
   * URL parameter schema for deep linking.
   * `list` selects the Notes list; `note` (PartnerMemo UUID) opens the
   * detail panel.
   */
  static urlParamSchema = {
    list: { type: 'enum', enum: ['received', 'sent'], default: 'received' },
    state: { type: 'enum', enum: ['active', 'archived'], default: 'active' },
    note: { type: 'string' },
    // `?info=1` opens the "About Notes" panel. Deep-linkable because the
    // dashboard introduction row routes straight here — Practices/Library/
    // Discovery keep click-only info panels and gain no URL param.
    info: { type: 'string' }
  };

  /**
   * Page-info tabs. Deliberately page-level content: what notes are, how they
   * are made, and what the recipient does and does not see. The inline chat-card
   * hint (`help-content/index.js` → NOTES) explains ONE card via an annotated
   * specimen, which is a different altitude — the same split practices and
   * insights already have.
   */
  /** Single-tab: buildPageInfoPanel hides the nav, so the panel reads as one
   * page — intro, how-it-works flow, CTA, FAQ. The old Writing/What-they-see
   * tab content lives on as FAQ answers. */
  static PAGE_INFO_TABS = {
    overview: {
      label: 'Overview',
      content: `
        <p class="page-info-detail__text">
          Notes are for the thing that is hard to say in the moment. They are
          not a running message thread, and they are not a place to keep score.
        </p>
        ${renderPageInfoFlow({
          steps: [
            {
              label: 'Talk it through',
              detail: `Work something out in an advice conversation — the
                option to write a note appears, or just ask for one.`,
            },
            {
              label: 'Read your draft',
              detail: `It's written in your voice, from what you actually
                said. Steer it, rewrite it, or discard it.`,
            },
            {
              label: 'Send it — or don’t',
              detail: 'Nothing reaches your partner until you choose to send.',
            },
          ],
        })}
        ${renderPageInfoFaq([
          {
            q: 'What does my partner see?',
            a: `Only the note you sent — never the conversation behind it,
              and never the drafts you discarded.`,
          },
          {
            q: 'Can I take a note back?',
            a: 'Yes. You can withdraw a note you have sent.',
          },
          {
            q: 'Will they know if I’ve read theirs?',
            a: `No. Reading is private in both directions — neither of you is
              shown whether the other has opened a note.`,
          },
          {
            q: 'Who actually writes it?',
            a: `You do, with help. The draft comes from what you said in the
              conversation, in your words — and you approve every word
              before it goes.`,
          },
        ])}
      `,
    },
  };

  /**
   * @param {Object} elements - DOM element references
   * @param {HTMLElement} elements.container - The #notes-page div
   * @param {EventBus} eventBus - Event bus for module communication
   */
  constructor(elements) {
    this.container = elements.container;
    this.discoveryApi = new DiscoveryAPI();

    this.isActive = false;

    /** @type {'received'|'sent'} Active notes list. */
    this.currentList = 'received';
    /** @type {'active'|'archived'} Archive filter (URL-routed). */
    this.currentState = 'active';
    /** Per-list lazy-fetch state: 'idle' | 'loading' | 'loaded' | 'error'. */
    this.listState = this._freshListState();

    /** @type {ResultsFilterBar|null} Shared filter/sort bar. */
    this.filterBar = null;

    /** @type {StickyStack|null} Sticky header stack (title → toggle → filter bar). */
    this.stickyStack = null;

    /** @type {boolean} Multi-select mode (checkboxes + bulk-action toolbar). */
    this.selectMode = false;
    /** @type {Set<string>} Selected note ids while in select mode. */
    this.selectedIds = new Set();

    /** @type {Object|null} Linked partner (names the discuss launcher, empty states, confirms). */
    this.partner = null;
    /** @type {Promise|null} In-flight partner fetch (awaited before detail open). */
    this._partnerPromise = null;

    /** Row options menu open/close controller (menu is a child of its `.note-row`). */
    this._rowMenu = new RowMenuController({
      container: () => this.container,
      keyAttr: 'data-note-id',
      rowSelector: '.note-row',
      hoverClose: true,
    });

    // Detail state
    this._panelOpen = false;
    /** @type {string|null} Note id currently shown in the panel. */
    this._panelNoteId = null;
    /** @type {'received'|'sent'|null} The list the open panel note belongs to
     * (may differ from the displayed list — a reply's parent opens cross-list). */
    this._panelList = null;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleContextMenu = this.handleContextMenu.bind(this);

    // Panel × close / cross-detail close — scrub the ?note= param so the URL
    // stays the source of truth. Skipped when not active (cross-section
    // navigation owns its own URL change).
    eventBus.on('right-panel:closed', (data) => {
      const isOurPanel = data?.contentType === CONTENT_TYPE
        || data?.contentType === INFO_CONTENT_TYPE;
      if (!isOurPanel || !this.isActive) return;
      this._panelOpen = false;
      this._panelNoteId = null;
      this._panelList = null;
      this.renderList();
      // Scrub whichever param drove the panel. Closing the info panel must NOT
      // resurrect a note detail — `info` and `note` are never both live, so
      // deleting both is always correct here.
      const url = new URL(window.location.href);
      let dirty = false;
      if (url.searchParams.has('note')) { url.searchParams.delete('note'); dirty = true; }
      if (url.searchParams.has('info')) { url.searchParams.delete('info'); dirty = true; }
      if (dirty) page.replace(url.pathname + (url.search ? url.search : ''));
    });

    // Universal cross-surface sync bridge (contentSync). /notes holds only
    // `note` items, stored with TIMESTAMP star/archive fields (coerce
    // boolean↔timestamp). Patch only lists whose status is 'loaded' — lists
    // lazy-load, so an idle list must no-op and fetch backend truth when opened
    // (never fabricate a row). Re-render only while active; refresh the visible
    // panel star header when the event targets the open note.
    eventBus.on(CONTENT_STARRED, ({ itemType, itemId, starred }) => {
      if (itemType !== 'note') return;
      // Refresh the open-panel star header FIRST — before the loaded/absent/
      // already-matching early-returns — so a self-emit with the note open (or
      // a cross-surface star of an open note) still flips the visible header.
      if (this._panelOpen && this._panelNoteId === itemId) {
        eventBus.emit('right-panel:set-star', { starred });
      }
      // Partner-note star is recipient-side only — never patch a sent note
      // (R2-Warning-5); only the received list can hold a starrable note.
      const rec = this.listState.received;
      if (rec.status !== 'loaded') return;
      const note = rec.notes.find((n) => n?.id === itemId);
      if (!note || Boolean(note.starredAt) === starred) return;
      note.starredAt = starred ? new Date().toISOString() : null;
      if (this.isActive) this.renderList();
    });

    eventBus.on(CONTENT_ARCHIVED, ({ itemType, itemId, archived }) => {
      if (itemType !== 'note') return;
      // Archive is per-side and applies to both sent + received.
      let changed = false;
      for (const list of ['received', 'sent']) {
        const state = this.listState[list];
        if (state.status !== 'loaded') continue;
        const note = state.notes.find((n) => n?.id === itemId);
        if (!note || Boolean(note.archivedAt) === archived) continue;
        note.archivedAt = archived ? new Date().toISOString() : null;
        changed = true;
      }
      if (changed && this.isActive) this.renderList();
    });

    eventBus.on(CONTENT_DELETED, ({ itemType, itemId }) => {
      if (itemType !== 'note') return;
      let changed = false;
      for (const list of ['received', 'sent']) {
        const state = this.listState[list];
        if (state.status !== 'loaded') continue;
        const before = state.notes.length;
        state.notes = state.notes.filter((n) => n?.id !== itemId);
        if (state.notes.length !== before) changed = true;
      }
      // `_bulkDelete` already removes the note from `listState` BEFORE
      // broadcasting, so a self-originated delete reaches here with nothing
      // left to remove (`changed` stays false) — bail out entirely (not just
      // skip the re-render) so it doesn't also emit a redundant second
      // `right-panel:close` alongside `_bulkDelete`'s own direct emit. A
      // genuinely foreign delete always finds the note (the panel can only be
      // open for a note in an already-`loaded` list — see applyURLParams),
      // so this never suppresses a real cross-surface close.
      if (!changed) return;
      // deactivate() nulls _panelNoteId, so this is a no-op when inactive.
      if (this._panelOpen && this._panelNoteId === itemId) {
        eventBus.emit('right-panel:close');
      }
      if (this.isActive) this.renderList();
    });

    eventBus.on(CONTENT_VIEWED, (data = {}) => {
      const { itemType, itemId, viewed } = data || {};
      if (itemType !== 'note' || (viewed !== true && viewed !== false)) return;
      const found = this._locateNote(itemId);
      if (!found || Boolean(found.note.viewedAt) === viewed) return;
      found.note.viewedAt = viewed ? new Date().toISOString() : null;
      if (this.isActive) this.renderList();
    });
  }

  /**
   * Fresh per-list fetch state (both lists unfetched).
   * @returns {Object} idle list state
   */
  _freshListState() {
    return {
      received: { status: 'idle', notes: [] },
      sent: { status: 'idle', notes: [] }
    };
  }

  /**
   * Activate the section. Skip-on-re-entry (Pattern 3): URL pushes within the
   * section re-fire the route handler, and list/panel state is driven by
   * `applyURLParams`, not re-activation.
   *
   * Couple gate: without an active partner link there is nothing to show —
   * redirect home (the sidebar item is hidden too; this covers deep links).
   */
  async activate() {
    if (this.isActive) return;
    if (!window.app?.currentUser?.coupleId) {
      page('/');
      return;
    }
    this.isActive = true;
    this.currentList = 'received';
    this.currentState = 'active';
    this.listState = this._freshListState();
    this.selectMode = false;
    this.selectedIds = new Set();
    this._panelOpen = false;
    this._panelNoteId = null;
    this.render();
    this._mountToolbarAction();
    this.container.addEventListener('click', this.handleClick);
    this.container.addEventListener('keydown', this.handleKeydown);
    this.container.addEventListener('contextmenu', this.handleContextMenu);
    this._partnerPromise = this._loadPartner();
  }

  /**
   * Deactivate: release listeners, tear down the filter bar (it owns a
   * document-level click listener), reset select mode, and close an open
   * detail panel. `isActive` flips first so the `right-panel:closed` listener
   * skips the URL scrub (the incoming section owns the URL). Idempotent.
   */
  deactivate() {
    this.isActive = false;
    this._unmountToolbarAction();
    this.container.removeEventListener('click', this.handleClick);
    this.container.removeEventListener('keydown', this.handleKeydown);
    this.container.removeEventListener('contextmenu', this.handleContextMenu);
    this.stickyStack?.destroy();
    this.stickyStack = null;
    if (this.filterBar) {
      this.filterBar.destroy();
      this.filterBar = null;
    }
    this.selectMode = false;
    this.selectedIds = new Set();
    this._rowMenu.close();
    if (this._panelOpen) {
      eventBus.emit('right-panel:close');
      this._panelOpen = false;
      this._panelNoteId = null;
    }
  }

  /**
   * Apply URL parameters — drives the Notes list and panel open/close.
   * Bidirectional: `note` present → open/swap detail; absent → close. Lists
   * are lazily fetched on first selection. Any list change exits select mode.
   *
   * @param {Object} params - Parsed URL parameters
   * @param {string} [params.list] - 'received' (default) or 'sent'
   * @param {string} [params.note] - Note (PartnerMemo) UUID to open in the detail panel
   * @param {string} [params.info] - '1' opens the About Notes panel; wins over `note`
   */
  async applyURLParams(params) {
    // activate() bails (couple-gate redirect) without setting isActive, but
    // the route handler still calls us afterwards — nothing to drive then.
    if (!this.isActive) return;

    const list = params.list === 'sent' ? 'sent' : 'received';
    const state = params.state === 'archived' ? 'archived' : 'active';
    if (list !== this.currentList || state !== this.currentState) {
      this.currentList = list;
      this.currentState = state;
      this._resetSelectMode();
      this.render();
    }

    if (this.listState[list].status === 'idle') {
      await this.loadList(list);
      if (!this.isActive) return;
    }

    // `info` and `note` are mutually exclusive and `info` WINS. Resolved here —
    // AFTER the list load above, so arriving at /notes?info=1 from the dashboard
    // still has a populated list waiting behind the panel — but BEFORE the note
    // branch, so showDetailView's mark-as-viewed side effect never fires for a
    // detail that would be immediately superseded.
    if (params.info) {
      // pushUrl:false — the param is already in the URL; re-pushing would add a
      // duplicate history entry for the state we arrived in.
      this.showPageInfo(false);
      const url = new URL(window.location.href);
      if (url.searchParams.has('note')) {
        url.searchParams.delete('note');
        page.replace(url.pathname + url.search);
      }
      return;
    }

    if (params.note) {
      if (this._panelOpen && this._panelNoteId === params.note) return;
      await this.showDetailView(params.note);
    } else if (this._panelOpen) {
      eventBus.emit('right-panel:close');
    }
  }

  /**
   * Open the "About Notes" panel.
   *
   * Unlike Practices/Library/Discovery this also writes `?info=1`, because the
   * dashboard introduction row deep-links here and the panel must survive a
   * reload or a back-navigation like every other /notes panel state.
   *
   * @param {boolean} [pushUrl=true] - False when called FROM applyURLParams
   *   (the param is already in the URL; re-pushing would double the history entry)
   */
  showPageInfo(pushUrl = true) {
    const content = buildPageInfoPanel({
      title: 'About Notes',
      intro: `Notes are short messages between you and your partner, written from
        what you work out in a conversation — in your words, and never sent
        without your say-so.`,
      tabs: PartnerNotesSection.PAGE_INFO_TABS
    });

    // Reading the full explanation retires the inline "What are partner notes?"
    // hint on chat cards — otherwise the user is offered a primer they just read.
    window.helpHintService?.markDismissed?.('notes');

    if (pushUrl) {
      const url = new URL(window.location.href);
      // `info` supersedes an open note detail — never both (see applyURLParams).
      url.searchParams.delete('note');
      url.searchParams.set('info', '1');
      page.replace(url.pathname + url.search);
    }

    this._panelOpen = true;
    this._panelNoteId = null;
    this._panelList = null;
    this.renderList();
    eventBus.emit('right-panel:open', {
      content,
      // MUST end in `-info`: PageManager keys off that suffix to let the active
      // section clear its routed detail before the panel content swaps.
      contentType: INFO_CONTENT_TYPE
    });
  }

  /**
   * Drop the routed note-detail selection without closing the panel.
   *
   * Called by PageManager when a page-info panel opens over an open note, so the
   * row highlight and `?note=` param don't outlive the detail they describe.
   *
   * `_panelOpen` deliberately stays TRUE — the panel is not closing, its content
   * is being replaced. Matching PracticesSection/InsightsSection; flipping it
   * false here would desync the toggle-close check in the row click handler.
   */
  clearActiveDetailSelection() {
    this._panelNoteId = null;
    this._panelList = null;
    if (this.isActive) this.renderList();

    const url = new URL(window.location.href);
    if (url.searchParams.has('note')) {
      url.searchParams.delete('note');
      page.replace(url.pathname + (url.search ? url.search : ''));
    }
  }

  /**
   * Clear select-mode state without re-rendering (callers re-render).
   */
  _resetSelectMode() {
    this.selectMode = false;
    this.selectedIds = new Set();
  }

  /**
   * Fetch the linked partner (names the discuss launcher, empty states,
   * bulk confirms, and legacy detail-title fallbacks). Non-fatal: a failure
   * leaves the 'your partner' fallback.
   */
  async _loadPartner() {
    try {
      const result = await this.discoveryApi.getPartner();
      if (!this.isActive) return;
      this.partner = result?.data || null;
      this.renderList();
    } catch (err) {
      console.error('[PartnerNotesSection] Failed to load partner:', err?.error || err);
    }
  }

  /**
   * Partner display name for empty states, confirms, and the discuss launcher.
   * @returns {string} Partner first name / name / username fallback chain
   */
  partnerDisplayName() {
    return this.partner?.firstName
      || this.partner?.name
      || this.partner?.username
      || 'your partner';
  }

  /**
   * The current user's own display name — used only for the detail-view
   * sign-off on sent notes (display-only chrome; the stored prose stays
   * name-free, see renderNoteDetailBody in partnerNotesDrillHelper).
   * @returns {string} First name / name / username fallback chain
   */
  ownDisplayName() {
    const user = window.app?.currentUser;
    return user?.firstName || user?.name || user?.username || 'you';
  }

  /**
   * Lazily fetch one list's notes. Per-list error isolation: a failed fetch
   * renders a retry affordance for that list only (never a fake empty state).
   * @param {'received'|'sent'} list - Which list to fetch
   */
  async loadList(list) {
    const state = this.listState[list];
    state.status = 'loading';
    this.renderList();
    try {
      const result = list === 'received'
        ? await this.discoveryApi.getMemos()
        : await this.discoveryApi.getSentMemos();
      if (!this.isActive) return;
      state.notes = Array.isArray(result?.data) ? result.data : [];
      state.status = 'loaded';
    } catch (err) {
      if (!this.isActive) return;
      console.error(`[PartnerNotesSection] Failed to load ${list} notes:`, err?.error || err);
      state.status = 'error';
    }
    this.renderList();
  }

  /**
   * Render the full page shell (header + Inbox/Sent toggle + filter bar +
   * list), remounting the bar area and the list.
   */
  render() {
    this.container.innerHTML = `
      <div class="explore-section">
        <div class="explore-container explore-container--fixed-action">
          <div class="explore-view--list" data-sticky-stack>
            ${renderPageInfoTitle('Notes')}
            <div class="page-header content-width">
              <div class="page-header__subtitle-row">
                <p class="page-header__subtitle">Sharing what matters</p>
              </div>
            </div>
            <div class="content-width partner-notes-content">
              <div class="page-toggle-row" data-sticky-slice data-sticky-collapse>
                <div class="page-toggle" role="tablist" aria-label="Note lists">
                  ${this.renderListButton('received', 'Inbox')}
                  ${this.renderListButton('sent', 'Sent')}
                </div>
              </div>
              <div data-filter-bar-mount="partner-notes" data-sticky-slice></div>
              <div class="partner-notes-list"></div>
            </div>
          </div>
          ${this.renderFixedAction()}
        </div>
      </div>
    `;
    this._mountBar();
    this.renderList();
    this._initStickyStack();
    Tooltip.setupAll(this.container, '[data-tooltip]', { position: 'below' });
  }

  /**
   * The fixed bottom "Compose Note" action — mobile-only (<1020px; desktop
   * shows the toolbar action instead, see _mountToolbarAction). Mirrors
   * /practices' page-fixed-action (reuses components/page-fixed-action.css).
   * Anchored to the shared .explore-container--fixed-action frame as a sibling
   * of the scroll container, so it stays put while the list scrolls beneath it.
   * The couple gate is
   * inherited from the page (uncoupled users never reach /notes).
   * @returns {string} HTML string
   */
  renderFixedAction() {
    return `
      <div class="page-fixed-action" data-fixed-page-action>
        <div class="page-fixed-action__inner">
          <button class="btn btn--primary page-fixed-action__button" data-action="compose-note" type="button">Compose Note</button>
        </div>
      </div>
    `;
  }

  /**
   * Mount the desktop "Compose Note" primary action into the page toolbar's
   * right slot (flush left of the X close button; hidden <1020px by CSS).
   * The slot lives outside `container`, so it survives list re-renders and is
   * mounted once per activation; PageManager.clearToolbarActions() also wipes
   * it on every page change.
   */
  _mountToolbarAction() {
    const slot = document.getElementById('page-toolbar-header-right-actions');
    if (!slot) return;
    slot.innerHTML = '';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn--primary btn--small btn--toolbar';
    btn.textContent = 'Compose Note';
    btn.addEventListener('click', () => launchComposeNoteAdvice());
    slot.appendChild(btn);
  }

  /** Remove the toolbar action mounted by _mountToolbarAction(). Idempotent. */
  _unmountToolbarAction() {
    const slot = document.getElementById('page-toolbar-header-right-actions');
    if (slot) slot.innerHTML = '';
  }

  /**
   * (Re)bind the sticky header stack: title → Inbox/Sent toggle → filter bar.
   * Idempotent — render() replaces `container.innerHTML` on every list/state
   * change, so the scroll root is a new node and the previous controller must
   * be released first.
   */
  _initStickyStack() {
    this.stickyStack?.destroy();
    this.stickyStack = null;
    const root = this.container.querySelector('.explore-view--list');
    if (root) this.stickyStack = new StickyStack(root);
  }

  /**
   * Render one Inbox ↔ Sent toggle button.
   * @param {'received'|'sent'} list - List id
   * @param {string} label - Visible label
   * @returns {string} HTML string
   */
  renderListButton(list, label) {
    const active = this.currentList === list;
    return `
      <button type="button" role="tab" aria-selected="${active}"
              class="page-toggle__btn ${active ? 'page-toggle__btn--active' : ''}"
              data-action="switch-list" data-list="${list}">${label}</button>
    `;
  }

  /**
   * Mount the bar area: the shared ResultsFilterBar (count + Active/Archived
   * filter + Select action) in normal mode, or the select-mode toolbar
   * ("N selected" + bulk actions).
   */
  _mountBar() {
    const mount = this.container.querySelector('[data-filter-bar-mount="partner-notes"]');
    if (!mount) return;
    if (this.filterBar) {
      this.filterBar.destroy();
      this.filterBar = null;
    }
    if (this.selectMode) {
      this._renderSelectToolbar(mount);
      return;
    }
    const { status, notes } = this.listState[this.currentList];
    this.filterBar = new ResultsFilterBar({
      container: mount,
      itemLabel: { singular: 'Note', plural: 'Notes' },
      count: status === 'loaded' ? notes.length : 0,
      filters: [
        {
          id: 'state',
          label: 'Status',
          defaultValue: this.currentState,
          options: [
            { value: 'active', label: 'Active' },
            { value: 'archived', label: 'Archived' }
          ]
        }
      ],
      actions: [
        { id: 'select', label: 'Select', icon: 'select', onClick: () => this._enterSelectMode() }
      ],
      onChange: (state) => this._handleFilterChange(state)
    });
  }

  /**
   * Render the select-mode toolbar in the bar mount: "N selected" on the
   * left; Star/Unstar (received) or Revoke (sent), Delete, Cancel on the
   * right. Bulk buttons disable at zero selection.
   * @param {HTMLElement} [mount] - Bar mount (looked up when omitted)
   */
  _renderSelectToolbar(mount = null) {
    const el = mount || this.container.querySelector('[data-filter-bar-mount="partner-notes"]');
    if (!el) return;

    const n = this.selectedIds.size;
    // No in-flight guard on this section — selection count is the only gate.
    const disabled = n === 0;
    const { notes } = this.listState[this.currentList];
    const selected = notes.filter((note) => this.selectedIds.has(note?.id));

    // The sent list has no star (you can't star your own outgoing note) —
    // Revoke takes the leading slot instead.
    let primaryAction;
    if (this.currentList === 'received') {
      const allStarred = n > 0 && selected.every((note) => note?.starredAt);
      primaryAction = {
        action: 'select-star',
        label: allStarred ? 'Unstar' : 'Star',
        icon: allStarred ? 'star' : 'star-outline',
        disabled
      };
    } else {
      primaryAction = { action: 'select-revoke', label: 'Revoke', icon: 'revoke', disabled };
    }

    // Archive label follows the VIEW here, not the selection.
    const archived = this.currentState === 'archived';
    mountSelectToolbar(el, {
      count: n,
      actions: [
        primaryAction,
        { action: 'select-archive', label: archived ? 'Unarchive' : 'Archive', icon: archived ? 'unarchive' : 'archive', disabled },
        { action: 'select-delete', label: 'Delete', icon: 'trash', disabled },
        { action: 'select-cancel', label: 'Cancel', icon: 'close' }
      ]
    });
  }

  /**
   * Enter multi-select mode: swap the bar for the bulk toolbar and re-render
   * rows with checkboxes.
   */
  _enterSelectMode() {
    if (this.selectMode) return;
    this.selectMode = true;
    this.selectedIds = new Set();
    this._mountBar();
    this.renderList();
  }

  /**
   * Exit multi-select mode: clear the selection and restore the filter bar.
   */
  _exitSelectMode() {
    this._resetSelectMode();
    this._mountBar();
    this.renderList();
  }

  /**
   * Toggle one note in the selection and refresh rows + toolbar.
   * @param {string} noteId - Note (PartnerMemo) UUID
   */
  _toggleSelected(noteId) {
    if (this.selectedIds.has(noteId)) {
      this.selectedIds.delete(noteId);
    } else {
      this.selectedIds.add(noteId);
    }
    this._renderSelectToolbar();
    this.renderList();
  }

  /**
   * Filter-bar change handler. The Active/Archived flip routes through the
   * URL (dropping any open `?note=` — the route handler's applyURLParams
   * pass closes the panel).
   * @param {{filters: {state: string}}} barState - Bar state
   */
  _handleFilterChange(barState) {
    const nextState = barState.filters.state === 'archived' ? 'archived' : 'active';
    if (nextState !== this.currentState) {
      page.replace(this.buildURL({ list: this.currentList, state: nextState }));
    }
  }

  /**
   * The current list's notes for the active Archive filter, newest first.
   * @returns {Object[]} Filtered + sorted copy of the active list's rows
   */
  getVisibleNotes() {
    const { notes } = this.listState[this.currentList];
    const wantArchived = this.currentState === 'archived';
    return notes
      .filter((note) => Boolean(note?.archivedAt) === wantArchived)
      .sort((a, b) => {
        const ta = new Date(a?.sentAt || a?.createdAt || 0).getTime();
        const tb = new Date(b?.sentAt || b?.createdAt || 0).getTime();
        return tb - ta;
      });
  }

  /**
   * Re-render the list area for the current list without rebuilding the page
   * shell, and sync the filter-bar count.
   */
  renderList() {
    const mount = this.container.querySelector('.partner-notes-list');
    if (!mount) return;

    this._rowMenu.close();
    // Container-agnostic select-mode modifier shared with /search (content-row.css).
    mount.classList.toggle('content-rows--select', this.selectMode);

    const { status } = this.listState[this.currentList];

    if (status === 'idle' || status === 'loading') {
      this.filterBar?.setCount(0);
      mount.innerHTML = `<p class="partner-notes__loading">Loading notes…</p>`;
      return;
    }
    if (status === 'error') {
      this.filterBar?.setCount(0);
      mount.innerHTML = `
        <div class="partner-notes__error">
          <p>Couldn’t load these notes right now.</p>
          <button type="button" class="partner-notes__retry" data-action="retry-list">Try again</button>
        </div>
      `;
      return;
    }

    const notes = this.getVisibleNotes();
    this.filterBar?.setCount(notes.length);

    if (notes.length === 0) {
      // renderEmptyState escapes its own text — the partner name goes in raw.
      const name = this.partnerDisplayName();
      let copy;
      if (this.currentState === 'archived') {
        copy = {
          // Archived is a non-neutral filter, so resetting brings the list back.
          filtered: !this.selectMode,
          title: 'No archived notes',
          description: 'Notes you archive will appear here. You can restore them any time.'
        };
      } else if (this.currentList === 'received') {
        copy = {
          title: 'No notes yet',
          description: `When ${name} shares a note with you, it’ll appear here.`
        };
      } else {
        copy = {
          // The old copy named a prerequisite and stopped there, leaving the
          // page a dead end for exactly the user who came here to learn what
          // notes are. The action is the conversation, not the note: a note
          // can't be composed cold — the composer needs material from the
          // sender's own turns.
          title: 'No notes yet',
          description: `Notes come out of advice conversations — when you work something out and want ${name} to understand it.`,
          action: { label: 'Start a conversation', dataAction: 'start-conversation' }
        };
      }
      mount.innerHTML = renderEmptyState(copy);
      return;
    }
    mount.innerHTML = `
      <div class="session-cards-container">
        ${notes.map((note) => this.renderNoteRow(note)).join('')}
      </div>
    `;
  }

  /**
   * Render one compact note row on the shared session-card system (same
   * hover/divider treatment as the /library patterns cards): a leading slot
   * (mail icon, swapping to the select checkbox in multi-select — the /search
   * icon-slot pattern shared with Practices), star marker, title
   * (medium-weight while unread; dimmed on retracted sent notes), and a
   * right-side meta that shows UNREAD/WITHDRAWN as a dot-label in place of the
   * relative date, then swaps to the shared chevron on hover.
   * @param {Object} note - Note row (received or sent shape)
   * @returns {string} HTML string
   */
  renderNoteRow(note) {
    const id = note?.id || '';
    const relDate = formatRelativeDate(note?.sentAt || note?.createdAt);
    const isOpenInPanel = Boolean(id) && id === this._panelNoteId;
    const activeCls = isOpenInPanel ? ' session-card--active' : '';
    const selected = this.selectMode && this.selectedIds.has(id);
    const selectedCls = selected ? ' session-card--active note-row--selected' : '';
    const unread = this.currentList === 'received' && !note?.viewedAt;
    const unreadCls = unread ? ' note-row--unread' : '';
    const withdrawn = this.currentList === 'sent' && Boolean(note?.revokedAt);
    const withdrawnCls = withdrawn ? ' note-row--withdrawn' : '';
    const statusIndicator = withdrawn
      ? '<span class="notification-label notification-label--gray">withdrawn</span>'
      : (unread ? '<span class="notification-label notification-label--blue">unread</span>' : '');
    const star = note?.starredAt
      ? `<span class="note-row__star" aria-label="Starred">${STAR_SVG}</span>`
      : '';
    // Reply-pair linkage is bidirectional. A reply names its parent; a root that
    // received a reply names the child. The linked title opens the OTHER note,
    // not the enclosing row. The API guarantees these directions are exclusive.
    const hasReplyLink = Boolean(note?.parentMemoId || note?.replyMemoId);
    const replyCls = hasReplyLink ? ' note-row--reply' : '';
    let replyLine = '';
    if (note?.parentMemoId) {
      replyLine = `<div class="note-row__reply-line">
          <span class="note-row__reply-arrow" aria-hidden="true">↳</span>
          <span class="note-row__reply-label">In reply to</span>
          <a class="note-row__reply-link" data-action="open-parent-note" data-parent-id="${escapeHtml(note.parentMemoId)}" role="button" tabindex="0">${note?.parentTitle ? `“${escapeHtml(note.parentTitle)}”` : 'an earlier note'}</a>
        </div>`;
    } else if (note?.replyMemoId) {
      const replyLabel = this.currentList === 'received'
        ? 'You replied'
        : `${escapeHtml(this.partnerDisplayName())} replied`;
      replyLine = `<div class="note-row__reply-line">
          <span class="note-row__reply-arrow" aria-hidden="true">↳</span>
          <span class="note-row__reply-label">${replyLabel}</span>
          <a class="note-row__reply-link" data-action="open-reply-note" data-reply-id="${escapeHtml(note.replyMemoId)}" role="button" tabindex="0">${note?.replyTitle ? `“${escapeHtml(note.replyTitle)}”` : 'with a note'}</a>
        </div>`;
    }
    const slot = this.selectMode
      ? `<span class="session-card__slot"><input type="checkbox" class="note-row__checkbox" tabindex="-1" aria-hidden="true"${selected ? ' checked' : ''}></span>`
      : `<span class="session-card__slot">${noteIconHtml(unread)}</span>`;
    const options = this.selectMode ? '' : this.renderRowOptions(note);

    return `
      <div class="card session-card note-row session-card--iconed${activeCls}${selectedCls}${unreadCls}${withdrawnCls}${replyCls}"
           data-action="open-note"
           data-note-id="${escapeHtml(id)}"
           role="button"
           tabindex="0"
           ${this.selectMode ? `aria-pressed="${selected}"` : `aria-expanded="${isOpenInPanel}"`}>
        ${slot}
        <div class="session-card__content">
          <div class="session-card__title-row note-row__title-row">
            ${star}
            <h4 class="session-card__title note-row__title">${escapeHtml(noteCardTitle(note))}</h4>
          </div>
          ${replyLine}
        </div>
        <span class="note-row__meta">
          ${statusIndicator
            ? `<span class="note-row__status">${statusIndicator}</span>`
            : (relDate ? `<span class="note-row__date">${escapeHtml(relDate)}</span>` : '')}
          ${options}
          <span class="session-card__chevron note-row__chevron" aria-hidden="true">${CHEVRON_SVG}</span>
        </span>
      </div>
    `;
  }

  /**
   * Render the hover-revealed kebab options button + its dropdown menu for
   * one row. Items largely mirror the multi-select toolbar for this list/view
   * (Star/Unstar or Retract, Archive/Unarchive, Delete) and run the exact
   * same action paths with a single-id selection. Received rows additionally
   * lead with Discuss — the same launcher as the detail-header "Discuss Note"
   * button (auto-sorted to the top by renderMenuItems). Markup reuses the
   * shared options-dropdown menu/item classes (dropdowns.css).
   * @param {Object} note - Note row
   * @returns {string} HTML string
   */
  renderRowOptions(note) {
    const id = note?.id || '';
    const items = [];
    if (this.currentList === 'received') {
      items.push({ action: 'row-discuss', label: 'Discuss' });
      items.push({ action: 'row-star', label: note?.starredAt ? 'Unstar' : 'Star', icon: note?.starredAt ? 'star' : 'star-outline' });
    } else if (!note?.revokedAt) {
      // Retract is not destructive text-wise (matches normal item color) — no danger variant.
      items.push({ action: 'row-revoke', label: 'Retract' });
    }
    items.push({ action: 'row-archive', label: note?.archivedAt ? 'Unarchive' : 'Archive' });
    items.push({ action: 'row-delete', label: 'Delete', variant: 'danger' });

    return renderRowKebab({ items, dataAttrs: { 'data-note-id': id }, ariaLabel: 'Note options' });
  }

  /**
   * Build the URL for a list/detail combination. Default values are omitted
   * so canonical URLs stay short (`/notes` = Received/Active).
   * @param {Object} [opts]
   * @param {'received'|'sent'} [opts.list] - Notes list
   * @param {'active'|'archived'} [opts.state] - Archive filter
   * @param {string} [opts.note] - Note id for the detail panel
   * @returns {string} Path + querystring
   */
  buildURL({ list = 'received', state = 'active', note = null } = {}) {
    const params = new URLSearchParams();
    if (list === 'sent') params.set('list', 'sent');
    if (state === 'archived') params.set('state', 'archived');
    if (note) params.set('note', note);
    const qs = params.toString();
    return `/notes${qs ? `?${qs}` : ''}`;
  }

  /**
   * Delegated click handler for the page (Inbox/Sent toggle, retry, rows,
   * and the select-mode toolbar actions).
   * @param {MouseEvent} event - Click event
   */
  handleClick(event) {
    const target = event.target.closest('[data-action]');
    if (!target || !this.container.contains(target)) return;

    switch (target.getAttribute('data-action')) {
      case 'switch-list': {
        const list = target.getAttribute('data-list');
        if (!list || list === this.currentList) return;
        // URL is the source of truth for list state; replace — a toggle
        // flip shouldn't grow history. Dropping ?note closes the panel via
        // the route handler's applyURLParams pass.
        page.replace(this.buildURL({ list, state: this.currentState }));
        break;
      }
      case 'retry-list':
        this.loadList(this.currentList);
        break;
      case 'compose-note':
        // The fixed bottom "Compose Note" action → launch a memo_compose ADVICE
        // session. The page is already couple-gated; the launcher re-checks too.
        launchComposeNoteAdvice();
        break;
      case 'start-conversation':
        // NOT a link to /chat: that route is `page.redirect('/')`, and for an
        // idle user handleBaseRoute falls through to the dashboard — so a /chat
        // CTA would bounce them back where they came from. The navigate event
        // sets pendingNavigation, which handleBaseRoute honours ahead of that.
        eventBus.emit('navigate', { page: 'chat', mode: 'ADVICE' });
        break;
      case 'clear-filters':
        // Same action as the filter menu's "Reset filters" row — one code path.
        this.filterBar?.reset();
        break;
      case 'open-note': {
        const noteId = target.getAttribute('data-note-id');
        if (!noteId) return;
        if (this.selectMode) {
          this._toggleSelected(noteId);
        } else {
          this.activateNoteRow(noteId);
        }
        break;
      }
      case 'open-parent-note':
      case 'open-reply-note': {
        // Reply-pair sublink — open the linked note from the OTHER list.
        // stopPropagation so the enclosing row's own note never opens instead.
        event.stopPropagation();
        if (this.selectMode) return;
        const linkedId = target.getAttribute('data-parent-id') || target.getAttribute('data-reply-id');
        if (linkedId) this.openLinkedNote(linkedId);
        break;
      }
      case 'row-options':
        event.stopPropagation();
        this._rowMenu.toggle(target);
        break;
      case 'row-discuss': {
        // Same effect as the detail-header "Discuss Note" button: launch an
        // ADVICE conversation seeded by this received memo. Received-only.
        event.stopPropagation();
        const noteId = target.getAttribute('data-note-id');
        this._rowMenu.close();
        if (noteId) this._discussNote(noteId);
        break;
      }
      case 'row-star': {
        event.stopPropagation();
        const noteId = target.getAttribute('data-note-id');
        this._rowMenu.close();
        if (noteId) this._bulkStar([noteId]);
        break;
      }
      case 'row-archive': {
        event.stopPropagation();
        const noteId = target.getAttribute('data-note-id');
        this._rowMenu.close();
        if (noteId) this._bulkArchive([noteId]);
        break;
      }
      case 'row-delete': {
        event.stopPropagation();
        const noteId = target.getAttribute('data-note-id');
        this._rowMenu.close();
        if (noteId) this._bulkDelete([noteId]);
        break;
      }
      case 'row-revoke': {
        event.stopPropagation();
        const noteId = target.getAttribute('data-note-id');
        this._rowMenu.close();
        if (noteId) this._bulkRevoke([noteId]);
        break;
      }
      case 'select-cancel':
        this._exitSelectMode();
        break;
      case 'select-star':
        this._bulkStar();
        break;
      case 'select-archive':
        this._bulkArchive();
        break;
      case 'select-delete':
        this._bulkDelete();
        break;
      case 'select-revoke':
        this._bulkRevoke();
        break;
      default:
        break;
    }
  }

  /**
   * Right-click a note row to open its kebab menu (same as the sidebar).
   * @param {MouseEvent} event
   */
  handleContextMenu(event) {
    this._rowMenu.handleContextMenu(event);
  }

  /**
   * Keyboard activation for the role="button" note rows (Enter / Space).
   * @param {KeyboardEvent} event - Keydown event
   */
  handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const row = event.target.closest('[data-action="open-note"]');
    if (!row || !this.container.contains(row)) return;
    // The reply-pair links are role=button anchors without hrefs, so explicitly
    // give them Enter/Space activation. Other inner actions activate natively.
    const inner = event.target.closest('[data-action]');
    if (inner && inner !== row) {
      if (inner.matches('[data-action="open-parent-note"], [data-action="open-reply-note"]')) {
        event.preventDefault();
        inner.click();
      }
      return;
    }
    event.preventDefault();
    const noteId = row.getAttribute('data-note-id');
    if (!noteId) return;
    if (this.selectMode) {
      this._toggleSelected(noteId);
    } else {
      this.activateNoteRow(noteId);
    }
  }

  /**
   * Post-action refresh shared by the bulk toolbar and the per-row options
   * menu: exit select mode when it's active (applying an action closes it),
   * otherwise just re-render the list in place.
   */
  _finishAction() {
    if (this.selectMode) {
      this._exitSelectMode();
    } else {
      this.renderList();
    }
  }

  /**
   * Bulk star/unstar the selection (received list; no confirm). Stars when
   * any selected note is unstarred; unstars when every one is already
   * starred. Applying the action exits select mode (like Delete/Revoke).
   * @param {string[]} [ids]
   * @returns {Promise<boolean>} true on success — used by the detail-header
   *   star toggle (`buildDetailPayload`) to know whether to revert its
   *   optimistic visual flip.
   */
  async _bulkStar(ids = [...this.selectedIds]) {
    if (ids.length === 0) return false;
    const idSet = new Set(ids);
    const { notes } = this.listState.received;
    const selected = notes.filter((note) => idSet.has(note?.id));
    const starred = !(selected.length > 0 && selected.every((note) => note?.starredAt));
    try {
      const result = await this.discoveryApi.starMemos(ids, starred);
      // A count of 0 (foreign/already-gone id — e.g. the header star toggle
      // racing a partner-side delete) is a silent no-op, not a thrown error;
      // treat it as failure so the caller (detail-header star button) reverts
      // its optimistic flip instead of showing a state nothing persisted.
      if (!(result?.data?.count > 0)) return false;
      // Apply-local-FIRST, then broadcast per note, BEFORE the `!isActive`
      // guard (R2-Warning-1) so other surfaces sync even mid-navigation. This
      // section's own listener self-skips (the note already matches).
      const now = new Date().toISOString();
      for (const note of selected) {
        note.starredAt = starred ? now : null;
        emitContentStarred('note', note.id, starred);
      }
      if (!this.isActive) return true;
      this._finishAction();
      return true;
    } catch (err) {
      console.error('[PartnerNotesSection] Bulk star failed:', err?.error || err);
      window.notificationManager?.showError('Failed to update stars. Please try again.');
      return false;
    }
  }

  /**
   * Bulk per-side archive/unarchive of the selection (no confirm). Archives
   * when viewing Active, unarchives when viewing Archived; either way the
   * rows leave the current view and select mode exits.
   */
  async _bulkArchive(ids = [...this.selectedIds]) {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    // Toggle semantics (mirrors star): unarchive only when every targeted note
    // is already archived. Equivalent to the view for multi-select (visible
    // rows always match the Active/Archived filter) and correct for
    // single-note calls from the detail-header Options menu.
    const targeted = this.listState[this.currentList].notes.filter((note) => idSet.has(note?.id));
    const archived = !(targeted.length > 0 && targeted.every((note) => note?.archivedAt));
    try {
      await this.discoveryApi.archiveMemos(ids, archived);
      // Apply-local-FIRST + broadcast per note, BEFORE the `!isActive` guard.
      const now = new Date().toISOString();
      for (const note of this.listState[this.currentList].notes) {
        if (idSet.has(note?.id)) {
          note.archivedAt = archived ? now : null;
          emitContentArchived('note', note.id, archived);
        }
      }
      if (!this.isActive) return;
      notifyArchived({ count: ids.length, type: 'note', archived });
      this._finishAction();
    } catch (err) {
      console.error('[PartnerNotesSection] Bulk archive failed:', err?.error || err);
      window.notificationManager?.showError('Failed to update notes. Please try again.');
    }
  }

  /**
   * Bulk per-side delete of the selection, behind a danger confirm. Removes
   * the notes from THIS user's list only, then exits select mode.
   */
  async _bulkDelete(ids = [...this.selectedIds]) {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    const n = ids.length;
    const name = this.partnerDisplayName();
    const confirmed = await modalService.confirm({
      title: `Delete ${n === 1 ? 'note' : `${n} notes`}`,
      message: `${n === 1 ? 'This note' : 'These notes'} will be removed from your list. ${name}’s copy isn’t affected.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!confirmed || !this.isActive) return;

    try {
      await this.discoveryApi.deleteMemos(ids);
      // Remove locally BEFORE broadcasting (so this section's own `content:deleted`
      // listener self-skips — finds nothing to remove → bails without a
      // redundant close) and BEFORE the `!isActive` guard.
      const state = this.listState[this.currentList];
      state.notes = state.notes.filter((note) => !idSet.has(note?.id));
      ids.forEach((id) => emitContentDeleted('note', id));
      if (!this.isActive) return;
      // Close the panel if the open note was among those deleted here (the
      // self-delete listener bailed, so this is the single close).
      if (this._panelOpen && idSet.has(this._panelNoteId)) {
        eventBus.emit('right-panel:close');
      }
      window.notificationManager?.showSuccess(n === 1 ? 'Note deleted' : `${n} notes deleted`);
      this._finishAction();
    } catch (err) {
      console.error('[PartnerNotesSection] Bulk delete failed:', err?.error || err);
      window.notificationManager?.showError('Failed to delete notes. Please try again.');
    }
  }

  /**
   * Bulk retract (soft-revoke) the selection (sent list), behind a danger
   * confirm. Marks the rows Withdrawn locally, then exits select mode.
   */
  async _bulkRevoke(ids = [...this.selectedIds]) {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    const n = ids.length;
    const name = this.partnerDisplayName();
    const confirmed = await modalService.confirm({
      title: `Revoke ${n === 1 ? 'note' : `${n} notes`}`,
      message: `This removes ${n === 1 ? 'the note' : 'them'} from ${name}’s view. ${name} may have already read ${n === 1 ? 'it' : 'them'}.`,
      confirmText: 'Revoke',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!confirmed || !this.isActive) return;

    try {
      await this.discoveryApi.revokeMemosBulk(ids);
      if (!this.isActive) return;
      const now = new Date().toISOString();
      for (const note of this.listState.sent.notes) {
        if (idSet.has(note?.id) && !note.revokedAt) {
          note.revokedAt = now;
        }
      }
      window.notificationManager?.showSuccess(n === 1 ? 'Note withdrawn' : `${n} notes withdrawn`);
      this._finishAction();
    } catch (err) {
      console.error('[PartnerNotesSection] Bulk revoke failed:', err?.error || err);
      window.notificationManager?.showError('Failed to withdraw notes. Please try again.');
    }
  }

  /**
   * Row activation (click or Enter/Space): open the note, or dismiss the panel
   * when the row already open is activated again — same path as the panel ×.
   * Deliberately NOT folded into `openNote`, so programmatic opens (the
   * reply-pair sublinks via `openLinkedNote`) stay pure opens and never toggle.
   * @param {string} noteId - Note (PartnerMemo) UUID
   */
  activateNoteRow(noteId) {
    if (this._panelOpen && this._panelNoteId === noteId) {
      eventBus.emit('right-panel:close');
      return;
    }
    this.openNote(noteId);
  }

  /**
   * Route a row activation into the panel-routed detail URL.
   * @param {string} noteId - Note (PartnerMemo) UUID
   */
  openNote(noteId) {
    // Dedup: page.js doesn't dedup identical navigations.
    if (this._panelOpen && this._panelNoteId === noteId) return;
    const url = this.buildURL({ list: this.currentList, state: this.currentState, note: noteId });
    // Push when the panel is closed (one history entry for "panel opened");
    // replace when swapping details so back always exits the panel in one step.
    if (this._panelOpen) {
      page.replace(url);
    } else {
      page(url);
    }
  }

  /**
   * Launch an ADVICE conversation seeded by a received note — the row-kebab
   * equivalent of the detail-header "Discuss Note" button, routed through the
   * same `launchMemoDiscussAdvice` launcher with the partner as the sender.
   * Received-list only (the kebab only offers Discuss there).
   * @param {string} noteId - Note (PartnerMemo) UUID
   */
  _discussNote(noteId) {
    launchMemoDiscussAdvice({ memoId: noteId, senderName: this.partnerDisplayName() });
  }

  /**
   * Open a linked note (a reply's parent, or a note's forward reply) in the right
   * panel WITHOUT switching the visible list. The linked note usually lives in the
   * OTHER list, but `showDetailView` resolves a note from either list, so the ?list
   * selector stays put and the user keeps their place — just a panel swap by id.
   * @param {string} noteId - The linked PartnerMemo UUID
   */
  openLinkedNote(noteId) {
    this.openNote(noteId);
  }

  /**
   * Find a note by id across BOTH loaded lists. A memo is only ever in one of them
   * (the user is its sender xor recipient), so the match is unambiguous.
   * @param {string} noteId - Note (PartnerMemo) UUID
   * @returns {{note: Object, list: 'received'|'sent'}|null}
   */
  _locateNote(noteId) {
    for (const list of ['received', 'sent']) {
      if (this.listState[list].status !== 'loaded') continue;
      const note = this.listState[list].notes.find((n) => n?.id === noteId);
      if (note) return { note, list };
    }
    return null;
  }

  /**
   * Open the right-panel detail for a note. Driven by `applyURLParams` whenever the
   * URL carries `?note=<id>`. Resolves the note from EITHER list (a reply's parent
   * lives in the other list; the panel shows it without switching the displayed
   * list), lazily loading the other list if needed. The payload uses the note's OWN
   * side (received/sent), not the displayed list, so its actions stay correct. An
   * unknown/absent id scrubs the param instead of opening.
   * @param {string} noteId - Note (PartnerMemo) UUID
   */
  async showDetailView(noteId) {
    // The discuss launcher names the partner — make sure the fetch settled first.
    if (this._partnerPromise) await this._partnerPromise;
    if (!this.isActive) return;

    let found = this._locateNote(noteId);
    if (!found) {
      // Not in the displayed list — a reply's parent lives in the other list.
      const other = this.currentList === 'received' ? 'sent' : 'received';
      if (this.listState[other].status === 'idle') {
        await this.loadList(other);
        if (!this.isActive) return;
      }
      found = this._locateNote(noteId);
    }
    if (!found) {
      console.warn('[PartnerNotesSection] Unknown note id in URL:', noteId);
      page.replace(this.buildURL({ list: this.currentList, state: this.currentState }));
      window.app?.modules?.rightPanelManager?.closeStaleHandoff();
      return;
    }
    const { note, list } = found;

    // Opening the detail is the "read" event for a RECEIVED note (keyed on the
    // note's OWN side, not the displayed list) — not awaited here so the panel
    // opens immediately; see `_markNoteViewed` for the confirmed-success gating.
    if (list === 'received' && !note.viewedAt) {
      this._markNoteViewed(noteId, note);
    }

    this._panelOpen = true;
    this._panelNoteId = noteId;
    this._panelList = list;
    this.renderList();
    eventBus.emit('right-panel:open', this.buildDetailPayload(note, list));
  }

  /**
   * Stamp `viewedAt` locally and broadcast `content:viewed` (sidebar Recents
   * dot) once the server confirms — not optimistically, matching the "never
   * broadcast an unconfirmed mutation" rule. Called without `await` from
   * `showDetailView` so opening the panel is never delayed by this network
   * round-trip; the row's unread state clears a tick later instead.
   * @param {string} noteId - PartnerMemo UUID
   * @param {Object} note - Note row from the current list (mutated on success)
   */
  async _markNoteViewed(noteId, note) {
    try {
      await this.discoveryApi.markMemoViewed(noteId);
    } catch (err) {
      console.error('[PartnerNotesSection] markMemoViewed failed:', err?.error || err);
      return;
    }
    note.viewedAt = new Date().toISOString();
    emitContentViewed('note', noteId, true);
    this.renderList();
  }

  /**
   * Build the `right-panel:open` payload for a note. Thin wrapper over the
   * shared `buildNoteDetailPayload` (partnerNotesDrillHelper) — the same
   * renderer /search uses — supplying this section's instance-bound callbacks:
   * Discuss (received), Retract (sent, → `_retractNote`), the received-only
   * header star toggle (→ `_bulkStar`, left of Options, mirrors the
   * session-detail panel; recipient-side only, so sent notes get no star), and
   * the Options dropdown (Archive/Unarchive, Delete → `_handleDetailOption`).
   * @param {Object} note - Note row from the current list
   * @returns {Object} right-panel payload
   */
  buildDetailPayload(note, list = this.currentList) {
    const partnerName = this.partnerDisplayName();
    return buildNoteDetailPayload(note, {
      list,
      partnerName,
      ownName: this.ownDisplayName(),
      contentType: CONTENT_TYPE,
      onDiscuss: () => launchMemoDiscussAdvice({ memoId: note.id, senderName: partnerName }),
      onStar: () => this._bulkStar([note.id]),
      onRetract: () => this._retractNote(note),
      onOpenLinkedNote: (noteId) => this.openLinkedNote(noteId),
      onOption: (action) => this._handleDetailOption(action, note)
    });
  }

  /**
   * Run a detail-header Options action through the same single-id paths as
   * the row kebab / multi-select, then refresh the open panel so the
   * Archive/Unarchive label stays current (delete closes the panel itself).
   * @param {'archive'|'delete'} action - Selected option
   * @param {Object} note - The note shown in the panel
   */
  async _handleDetailOption(action, note) {
    if (action === 'archive') {
      await this._bulkArchive([note.id]);
    } else if (action === 'delete') {
      await this._bulkDelete([note.id]);
      return;
    }
    if (this._panelOpen && this._panelNoteId === note.id) {
      eventBus.emit('right-panel:open', this.buildDetailPayload(note, this._panelList));
    }
  }

  /**
   * Revoke a sent note from its open detail panel (injected as the shared
   * helper's `onRetract`). On success mutates the row, refreshes the list, and
   * re-opens the rebuilt (Withdrawn, no-Retract) payload — a content cross-fade,
   * not a new slide-in. A 409 (vanished server-side) is terminal: refresh the
   * list and close the detail, resolving so the confirm buttons stay disabled.
   * Any other failure rethrows so the helper re-enables the confirm buttons.
   * @param {Object} note - The sent note row (mutated on success)
   * @returns {Promise<void>}
   */
  async _retractNote(note) {
    try {
      const result = await this.discoveryApi.revokeMemo(note.id);
      if (!this.isActive) return;
      note.revokedAt = result?.data?.revokedAt || new Date().toISOString();
      window.notificationManager?.showSuccess('Note withdrawn');
      this.renderList();
      eventBus.emit('right-panel:open', this.buildDetailPayload(note, this._panelList));
    } catch (err) {
      if (!this.isActive) return;
      if (err?.status === 409) {
        // Note vanished server-side; refresh the list and close the detail.
        // Resolve (don't rethrow) — the panel is going away, so the confirm
        // buttons shouldn't re-enable.
        window.notificationManager?.showError('This note is no longer available.');
        this.listState.sent.status = 'idle';
        page.replace(this.buildURL({ list: 'sent', state: this.currentState }));
        this.loadList('sent');
        return;
      }
      console.error('[PartnerNotesSection] Retract failed:', err?.error || err);
      window.notificationManager?.showError('Failed to withdraw note. Please try again.');
      throw err;
    }
  }
}
